import { useTranslation } from "react-i18next";

interface TherapistCardProps {
  therapist: {
    id: string;
    nombre?: string;
    apellidos?: string | null;
    nombre_centro?: string | null;
    ciudad?: string | null;
    tipos_masaje?: string[] | null;
    bio?: string | null;
    anos_experiencia?: number | null;
    precio_desde?: number | null;
    calificacion_promedio?: number | null;
    total_resenas?: number | null;
    foto_portada?: string | null;
    es_centro_masajes?: boolean | null;
    es_destacado?: boolean | null;
    is_demo?: boolean | null;
  };
}

const TherapistCard = ({ therapist }: TherapistCardProps) => {
  const { t, i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || i18n.language || "es").split("-")[0];
  const {
    nombre,
    apellidos,
    nombre_centro,
    ciudad,
    tipos_masaje,
    anos_experiencia,
    precio_desde,
    calificacion_promedio,
    total_resenas,
    foto_portada,
    es_centro_masajes,
  } = therapist;

  const displayName = es_centro_masajes && nombre_centro
    ? nombre_centro
    : [nombre, apellidos].filter(Boolean).join(" ");

  const tags = tipos_masaje ?? [];
  const reviews = total_resenas ?? 0;
  const rating = calificacion_promedio ?? 0;

  return (
    <article className="overflow-hidden rounded-xl border border-border bg-card shadow-soft">
      <div className="relative aspect-[4/3] w-full bg-muted">
        {foto_portada ? (
          <img
            src={foto_portada}
            alt={t("therapistCardV2.photoAlt", { name: displayName })}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <span className="font-serif text-5xl text-muted-foreground">
              {(displayName || "?").charAt(0).toUpperCase()}
            </span>
          </div>
        )}

      </div>

      <div className="space-y-3 p-4">
        <div>
          <h3 className="font-serif text-lg leading-tight text-foreground">
            {displayName}
          </h3>
          {ciudad && (
            <p className="mt-1 text-xs text-muted-foreground">{ciudad}</p>
          )}
          {reviews > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              {rating.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} · {reviews} {t("therapistCardV2.reviews", { count: reviews })}
            </p>
          )}
        </div>

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {tags.slice(0, 3).map((tipo) => (
              <span
                key={tipo}
                className="rounded-full bg-muted px-2.5 py-1 text-[11px] text-muted-foreground"
              >
                {t(`massageTypes.${tipo}`, {
                  defaultValue: tipo.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()),
                })}
              </span>
            ))}
            {tags.length > 3 && (
              <span className="rounded-full bg-muted px-2.5 py-1 text-[11px] text-muted-foreground">
                +{tags.length - 3}
              </span>
            )}
          </div>
        )}

        {(anos_experiencia || precio_desde) && (
          <div className="flex items-center justify-between border-t border-border pt-3 text-xs text-muted-foreground">
            <span>
              {anos_experiencia ? t("therapistCardV2.experience", { count: anos_experiencia }) : ""}
            </span>
            <span>{precio_desde ? t("therapistCardV2.from", { price: precio_desde }) : ""}</span>
          </div>
        )}
      </div>
    </article>
  );
};

export default TherapistCard;
